import { useEffect, useState } from "react";
import { api, brl } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";

const SERVICOS = [
  { key: "montagem", label: "Montagem de móveis" },
  { key: "desmontagem", label: "Desmontagem de móveis" },
  { key: "embalagem", label: "Embalagem de móveis e objetos" },
];
const SERVICO_LABEL_SHORT = { montagem: "Montagem", desmontagem: "Desmontagem", embalagem: "Embalagem" };

const inputCls =
  "mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

function Toggle({ value, onChange, testId, showLabel }) {
  return (
    <span className="flex items-center gap-2 shrink-0">
      {showLabel && (
        <span data-testid={`${testId}-estado`} className={`text-[10px] font-extrabold uppercase ${value ? "text-emerald-600" : "text-slate-400"}`}>
          {value ? "Habilitado" : "Desabilitado"}
        </span>
      )}
      <button
        type="button"
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${value ? "bg-[#F5B301]" : "bg-slate-200"}`}
      >
        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${value ? "left-6" : "left-1"}`} />
      </button>
    </span>
  );
}

function ServicosForm({ user }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/servicos-adicionais").then((r) => setCfg(r.data)).catch(() => toast.error("Falha ao carregar"));
  }, []);

  if (!cfg) {
    return (
      <AdminShell user={user} title="Gerenciar Serviços">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  const setServico = (k, patch) => setCfg({ ...cfg, servicos: { ...cfg.servicos, [k]: { ...cfg.servicos[k], ...patch } } });
  const setItem = (k, idx, patch) => {
    const itens = [...(cfg.servicos[k]?.itens || [])];
    itens[idx] = { ...itens[idx], ...patch };
    setServico(k, { itens });
  };
  const addItem = (k) => setServico(k, { itens: [...(cfg.servicos[k]?.itens || []), { id: "", nome: "", preco: "", ativo: true }] });
  const removeItem = (k, idx) => setServico(k, { itens: cfg.servicos[k].itens.filter((_, i) => i !== idx) });

  const setPacote = (idx, patch) => {
    const pacotes = [...cfg.pacotes];
    pacotes[idx] = { ...pacotes[idx], ...patch };
    setCfg({ ...cfg, pacotes });
  };
  const togglePacoteServico = (idx, srv) => {
    const atuais = cfg.pacotes[idx].servicos || [];
    setPacote(idx, { servicos: atuais.includes(srv) ? atuais.filter((s) => s !== srv) : [...atuais, srv] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/servicos-adicionais", {
        servicos: cfg.servicos,
        pacotes_ativo: cfg.pacotes_ativo,
        pacotes: cfg.pacotes,
      });
      setCfg(r.data);
      toast.success("Serviços adicionais salvos!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const toggleRapido = async (novoCfg, mensagem) => {
    setCfg(novoCfg);
    try {
      const r = await api.put("/admin/servicos-adicionais", {
        servicos: novoCfg.servicos,
        pacotes_ativo: novoCfg.pacotes_ativo,
        pacotes: novoCfg.pacotes,
      });
      setCfg(r.data);
      toast.success(mensagem);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar a alteração");
      api.get("/admin/servicos-adicionais").then((r) => setCfg(r.data)).catch(() => {});
    }
  };

  const toggleServicoRapido = (key, ativo) => {
    const novo = { ...cfg, servicos: { ...cfg.servicos, [key]: { ...cfg.servicos[key], ativo } } };
    const label = SERVICOS.find((s) => s.key === key)?.label || key;
    toggleRapido(novo, `${label}: ${ativo ? "habilitado" : "desabilitado"} com sucesso!`);
  };

  return (
    <AdminShell user={user} title="Montagem e Embalagem">
      <div className="px-4 mt-4 space-y-4" data-testid="admin-servicos-form">
        <p className="text-[11px] text-slate-400">
          Ative os serviços, cadastre itens com preços e monte pacotes. Somente o que estiver ativo aparece no orçamento do cliente.
        </p>

        {SERVICOS.map(({ key, label }) => (
          <div key={key} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-testid={`servicos-card-${key}`}>
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400">{label}</h3>
              <Toggle testId={`servicos-toggle-${key}`} showLabel value={!!cfg.servicos[key]?.ativo} onChange={(v) => toggleServicoRapido(key, v)} />
            </div>
            {(cfg.servicos[key]?.itens || []).map((item, idx) => (
              <div key={idx} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`servicos-item-${key}-${idx}`}>
                <div className="flex items-center gap-2">
                  <input
                    data-testid={`servicos-item-nome-${key}-${idx}`}
                    className={inputCls}
                    placeholder="Ex.: Guarda-roupa"
                    value={item.nome}
                    onChange={(e) => setItem(key, idx, { nome: e.target.value })}
                  />
                  <button type="button" data-testid={`servicos-item-del-${key}-${idx}`} onClick={() => removeItem(key, idx)}
                    className="p-2 text-red-400 hover:text-red-600 shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    data-testid={`servicos-item-preco-${key}-${idx}`}
                    type="number" min="0" step="0.01"
                    className={inputCls}
                    placeholder="R$"
                    value={item.preco}
                    onChange={(e) => setItem(key, idx, { preco: e.target.value })}
                  />
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">Ativo</span>
                    <Toggle testId={`servicos-item-ativo-${key}-${idx}`} value={!!item.ativo} onChange={(v) => setItem(key, idx, { ativo: v })} />
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              data-testid={`servicos-item-add-${key}`}
              onClick={() => addItem(key)}
              className="w-full py-2.5 rounded-xl border-2 border-dashed border-[#F5B301] text-navy text-xs font-bold active:scale-95 transition-transform flex items-center justify-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> ADICIONAR ITEM
            </button>
          </div>
        ))}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-testid="servicos-pacotes-card">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400">Pacotes de serviços</h3>
            <Toggle testId="servicos-toggle-pacotes" showLabel value={!!cfg.pacotes_ativo}
              onChange={(v) => toggleRapido({ ...cfg, pacotes_ativo: v }, `Pacotes ${v ? "habilitados" : "desabilitados"} com sucesso!`)} />
          </div>
          <p className="text-[11px] text-slate-400">
            Pacote tem preço fechado e substitui os itens individuais dos serviços incluídos (sem cobrança dupla).
          </p>
          {(cfg.pacotes || []).map((p, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`servicos-pacote-${idx}`}>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`servicos-pacote-nome-${idx}`}
                  className={inputCls}
                  placeholder="Ex.: Pacote completo"
                  value={p.nome}
                  onChange={(e) => setPacote(idx, { nome: e.target.value })}
                />
                <button type="button" data-testid={`servicos-pacote-del-${idx}`} onClick={() => setCfg({ ...cfg, pacotes: cfg.pacotes.filter((_, i) => i !== idx) })}
                  className="p-2 text-red-400 hover:text-red-600 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {SERVICOS.map(({ key }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`servicos-pacote-srv-${idx}-${key}`}
                    onClick={() => togglePacoteServico(idx, key)}
                    className={`px-3 py-1.5 rounded-full border text-[11px] font-bold transition-all ${
                      (p.servicos || []).includes(key) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 text-slate-400"
                    }`}
                  >
                    {SERVICO_LABEL_SHORT[key]}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid={`servicos-pacote-preco-${idx}`}
                  type="number" min="0" step="0.01"
                  className={inputCls}
                  placeholder="R$ preço fechado"
                  value={p.preco}
                  onChange={(e) => setPacote(idx, { preco: e.target.value })}
                />
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">Ativo</span>
                  <Toggle testId={`servicos-pacote-ativo-${idx}`} value={!!p.ativo} onChange={(v) => setPacote(idx, { ativo: v })} />
                </div>
              </div>
              {(p.servicos || []).length > 0 && p.preco > 0 && (
                <p className="text-[11px] text-slate-400">
                  {p.nome || "Pacote"}: {(p.servicos || []).map((s) => SERVICO_LABEL_SHORT[s]).join(" + ")} por {brl(parseFloat(p.preco) || 0)}
                </p>
              )}
            </div>
          ))}
          <button
            type="button"
            data-testid="servicos-pacote-add"
            onClick={() => setCfg({ ...cfg, pacotes: [...(cfg.pacotes || []), { id: "", nome: "", servicos: [], preco: "", ativo: true }] })}
            className="w-full py-2.5 rounded-xl border-2 border-dashed border-[#F5B301] text-navy text-xs font-bold active:scale-95 transition-transform flex items-center justify-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> ADICIONAR PACOTE
          </button>
        </div>

        <button
          data-testid="save-servicos-btn"
          onClick={save}
          disabled={saving}
          className="w-full py-4 rounded-2xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> SALVAR SERVIÇOS
        </button>
      </div>
    </AdminShell>
  );
}

export default function AdminServicos() {
  return <AdminGate>{(user) => <ServicosForm user={user} />}</AdminGate>;
}
